/**
 * Bringing things into existence (docs/ux/intake.md): the queries and
 * mutations behind New case, New doctor, New user, the referrer and clinic
 * writes, and the demo prefill the four dialogs share.
 *
 * The prefill query is allowed to fail quietly. The demo tools are absent
 * outside a demo build, so a 404 here is the ordinary state, not an error to
 * put in front of anyone: the button is simply not rendered.
 */

import type { ReportTypeKind } from '@beam/core/status';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/app/api';
import { useEnvironment } from '@/app/environment';
import type { ClinicRecord, ReferrerRecord } from '@/app/types';
import { doctorsKey } from '../registry/useRegistry';

// The registry hooks spell these inline rather than exporting them; the same
// arrays, so an invalidation here reaches the same caches.
const referrersKey = ['registry', 'referrers'] as const;
const clinicsKey = ['registry', 'clinics'] as const;

export type DemoFile = { name: string; bytes: number; pdf: boolean };
export type Prefill = {
  files: DemoFile[];
  case: { matterRef: string; patientAsOnLoi: string; referrerId: string; doctorSlug: string; reportType: ReportTypeKind; dueDate: string; letter: string | null };
  doctor: { name: string; slug: string };
};

export const prefillKey = ['demo', 'prefill'] as const;

/**
 * What the demo prefix holds. Gated on the environment rather than probing the
 * route: /api/demo/prefill is absent outside a demo build, and a 404 is a
 * console error on every page carrying the button, whatever the caller does
 * with the rejection. The pipeline and registry smoke tests assert the console
 * is clean, and they were right to.
 */
export function useDemoPrefill() {
  const environment = useEnvironment();
  return useQuery({
    queryKey: prefillKey,
    queryFn: () => api<Prefill>('/api/demo/prefill'),
    enabled: environment.data?.demoTools === true,
    retry: false,
    staleTime: 30_000,
  });
}

export type NewCaseInput = { matterRef: string; patientAsOnLoi: string; referrerId: string; doctorSlug: string; reportType: ReportTypeKind; dueDate: string };

export function useCreateCase() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: NewCaseInput) => api<{ matter: { ref: string } }>('/api/matters', { method: 'POST', json: input }),
    // The board, the queues and every count are folds over the matters, so they all go stale at once.
    onSuccess: () => { void client.invalidateQueries({ queryKey: ['matters'] }); void client.invalidateQueries({ queryKey: ['pipeline'] }); },
  });
}

export function useCreateDoctor() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { slug: string; name: string }) => api<{ slug: string }>('/api/registry/doctors', { method: 'POST', json: input }),
    onSuccess: () => { void client.invalidateQueries({ queryKey: doctorsKey }); },
  });
}

export function useCreateReferrer() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; kind: 'standard' | 'plaintiff'; slaDays?: number }) => api<ReferrerRecord>('/api/registry/referrers', { method: 'POST', json: input }),
    onSuccess: () => { void client.invalidateQueries({ queryKey: referrersKey }); },
  });
}

export function useUpdateReferrer() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; name: string; kind: 'standard' | 'plaintiff'; slaDays?: number }) =>
      api<ReferrerRecord>(`/api/registry/referrers/${encodeURIComponent(vars.id)}`, { method: 'PUT', json: { name: vars.name, kind: vars.kind, slaDays: vars.slaDays } }),
    onSuccess: () => { void client.invalidateQueries({ queryKey: referrersKey }); },
  });
}

export function useCreateClinic() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; suburb: string }) => api<ClinicRecord>('/api/registry/clinics', { method: 'POST', json: input }),
    onSuccess: () => { void client.invalidateQueries({ queryKey: clinicsKey }); },
  });
}

export function useUpdateClinic() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; name: string; suburb: string }) =>
      api<ClinicRecord>(`/api/registry/clinics/${encodeURIComponent(vars.id)}`, { method: 'PUT', json: { name: vars.name, suburb: vars.suburb } }),
    onSuccess: () => { void client.invalidateQueries({ queryKey: clinicsKey }); },
  });
}

/** Creating a sign-in. Nothing about the password is cached or kept after the call. */
export function useCreateUser() {
  return useMutation({
    mutationFn: (input: { name: string; email: string; persona: 'admin' | 'qa' | 'doctor'; doctorSlug: string | null; password: string }) =>
      api<{ email: string }>('/api/demo/users', { method: 'POST', json: input }),
  });
}

/** Copy a demo letter onto a case, server side, so the bytes never travel through the browser. */
export function useLetterFromDemo(matterRef: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => api(`/api/matters/${encodeURIComponent(matterRef)}/loi/files/letter/from-demo`, { method: 'POST', json: { name } }),
    onSuccess: () => { void client.invalidateQueries({ queryKey: ['matter', matterRef] }); },
  });
}

/** Put the recorded run on the case and import it, so the casefile tab stops pointing at a CLI. */
export function useSortFromDemo(matterRef: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (runId: string) => api<{ jobId: string }>(`/api/matters/${encodeURIComponent(matterRef)}/sort/from-demo`, { method: 'POST', json: { runId } }),
    onSuccess: () => { void client.invalidateQueries({ queryKey: ['matter', matterRef] }); },
  });
}
